import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { MailCheck, Loader2, RotateCw, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Pagina mostrata subito dopo il signup email/password.
 * Chiede all'utente di controllare la casella email per confermare l'account.
 * Espone anche un pulsante per rispedire l'email di verifica.
 */
export default function CheckEmailPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error('Email non specificata. Torna al login.');
      return;
    }
    setResending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setResent(true);
        toast.success('Email di verifica reinviata. Controlla la tua casella.');
      } else {
        toast.error('Impossibile reinviare l\'email. Riprova tra qualche minuto.');
      }
    } catch (e) {
      toast.error('Errore di rete. Riprova.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div
        className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm"
        data-testid="check-email-page"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 text-teal-600 mb-4">
          <MailCheck className="w-9 h-9" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">
          Controlla la tua casella email
        </h1>
        <p className="text-slate-600 leading-relaxed mb-6">
          Ti abbiamo inviato un link di conferma
          {email ? (
            <> all&apos;indirizzo <span className="font-semibold text-slate-800">{email}</span></>
          ) : null}
          . Clicca sul link nell&apos;email per attivare il tuo account Rarifex.
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left text-sm text-amber-900 mb-6">
          <p className="font-semibold mb-1">Non trovi l&apos;email?</p>
          <ul className="list-disc list-inside space-y-1 text-amber-800">
            <li>Controlla la cartella <strong>Spam</strong> o <strong>Promozioni</strong>.</li>
            <li>Verifica di aver scritto correttamente l&apos;indirizzo email.</li>
            <li>Attendi qualche minuto: la consegna può richiedere fino a 5 minuti.</li>
          </ul>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleResend}
            disabled={resending || resent || !email}
            className="w-full bg-shop-primary hover:bg-shop-primary/90 text-white"
            data-testid="check-email-resend-btn"
          >
            {resending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : resent ? (
              'Email reinviata ✓'
            ) : (
              <>
                <RotateCw className="w-4 h-4 mr-2" />
                Rispedisci email di conferma
              </>
            )}
          </Button>

          <Button
            asChild
            variant="outline"
            className="w-full"
            data-testid="check-email-login-btn"
          >
            <Link to="/login">Ho già confermato, vai al login</Link>
          </Button>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors mt-2"
            data-testid="check-email-home-btn"
          >
            <Home className="w-4 h-4" />
            Torna alla home
          </Link>
        </div>
      </div>
    </div>
  );
}
